import React from 'react';
import { HiOutlineExclamationCircle } from '../icons';

export interface TextareaProps extends React.ComponentProps<'textarea'> {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
  variant?: 'default' | 'filled' | 'outlined';
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
}

const Textarea = React.forwardRef<React.ElementRef<'textarea'>, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      required = false,
      fullWidth = false,
      variant = 'default',
      resize = 'vertical',
      autoResize = false,
      minRows = 3,
      maxRows = 10,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const textareaRef = React.useRef<React.ElementRef<'textarea'>>(null);

    React.useImperativeHandle(ref, () => textareaRef.current!);

    const adjustHeight = React.useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea || !autoResize) return;

      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
      const minHeight = lineHeight * minRows;
      const maxHeight = lineHeight * maxRows;

      textarea.style.height = `${Math.min(Math.max(scrollHeight, minHeight), maxHeight)}px`;
    }, [autoResize, minRows, maxRows]);

    React.useEffect(() => {
      adjustHeight();
    }, [adjustHeight]);

    const baseClasses =
      'block w-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = {
      default: 'border border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-500',
      filled: 'border-0 bg-gray-100 focus:bg-white focus:ring-blue-500',
      outlined: 'border-2 border-gray-300 bg-transparent focus:border-blue-500 focus:ring-blue-500',
    };

    const resizeClasses = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };

    const errorClasses = error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '';

    const widthClass = fullWidth ? 'w-full' : '';
    const resizeClass = autoResize ? 'resize-none' : resizeClasses[resize];

    const textareaClasses =
      `${baseClasses} ${variantClasses[variant]} ${errorClasses} ${widthClass} ${resizeClass} px-4 py-2 text-sm rounded-lg ${className}`.trim();

    const containerClasses = fullWidth ? 'w-full' : '';

    return (
      <div className={containerClasses}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={textareaRef}
          disabled={disabled}
          rows={autoResize ? minRows : props.rows || minRows}
          className={textareaClasses}
          onInput={autoResize ? adjustHeight : undefined}
          {...props}
        />

        {(error || helperText) && (
          <div className="mt-1 flex items-center">
            {error && (
              <>
                <HiOutlineExclamationCircle className="h-4 w-4 text-red-500 mr-1" />
                <span className="text-sm text-red-600">{error}</span>
              </>
            )}
            {!error && helperText && <span className="text-sm text-gray-500">{helperText}</span>}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
